'use client';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  Dispatch,
} from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { useProspectSearch } from 'src/context/prospects';
import {
  useGetProfileTabDataQuery,
  PROFILE_TAB_DATA_VIEWS,
} from 'src/api/redux/profiles';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import { SOC_COMPOSITE_SEPARATOR } from 'src/components/FilterSets/SiteOfCare/utils';
import {
  CODE_FILTER_GROUPS,
  MAX_PROFILE_CODE_SET_SIZE,
} from 'src/modules/Profile/constants';

const defaultFilters = {
  years: {
    title: 'Years',
    filters: [],
    selectedDict: {},
  },
  categories: {
    title: 'Category',
    filters: [],
    selectedDict: {},
  },
  drug: {
    title: 'Drug',
    filters: [],
    selectedDict: {},
  },
  paymentCategories: {
    title: 'Payment Type',
    filters: [],
    selectedDict: {},
  },
  code: {
    title: 'Latest Related Search Codes',
    filters: [],
    selectedDict: {},
  },
  sources: {
    title: 'Payer',
    filters: [],
    selectedDict: {},
  },
  paymentSources: {
    title: 'Company',
    filters: [],
    selectedDict: {},
  },
  place_of_service: {
    title: 'Place of Service',
    filters: [],
    selectedDict: {},
  },
  // SOC replacement for place_of_service when use-site-of-care is on. Defined
  // alongside place_of_service so the FilterSet type covers both keys; only
  // one is populated/used at runtime depending on the flag.
  site_of_care: {
    title: 'Site of Care Type',
    filters: [],
    selectedDict: {},
  },
  // For payments view
  product_types: {
    title: 'Product Type',
    filters: [],
    selectedDict: {},
  },
  product_names: {
    title: 'Product Name',
    filters: [],
    selectedDict: {},
  },
  inpatient_flag: {
    title: 'Inpatient Status',
    filters: [],
    selectedDict: {},
  },
};
export type FilterSet = typeof defaultFilters;
const FilterContext = createContext<[FilterSet, Dispatch<FilterSet>]>([
  defaultFilters,
  () => null,
]);
// TODO: Convert to useReducer
export const FilterProvider = (props) => {
  const filterState = useState(defaultFilters);
  const { view, type, id } = useParams<{ view: string; type: string; id: string }>();
  const searchParams = useSearchParams();
  const categories = searchParams.get('categories') as string;
  const relationship = (searchParams.get('relationship') as 'inbound' | 'outbound') || 'inbound';
  const { prospectFilters, queryString } = useProspectSearch();

  const { data } = useGetProfileTabDataQuery(
    {
      id,
      type,
      view,
      relationship,
      filters: null,
      queryString,
    },
    {
      skip: !id || !type || !view || !PROFILE_TAB_DATA_VIEWS.has(view),
    }
  );

  const [, setFilters] = filterState;
  const { selectedFilterSet } = useProfileSelections(`${type}-${view}`);

  const truncatedText = (text, length) => {
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const searchName = useMemo(() => {
    return truncatedText(
      prospectFilters
        .filter((f) => f.context !== 'spec')
        .map((filter) => filter.title)
        .join(', '),
      25
    );
  }, [prospectFilters]);

  const parseAttributeFilters = useCallback(
    (allFilters) => {
      if (!allFilters) return;
      const searchFilters = prospectFilters.filter((f) => f.context !== 'spec');
      const attributeFilters = Object.keys(allFilters).reduce((accum, key) => {
        const filters = allFilters[key];

        // Payments category special case
        let conditionalKey = key;
        if (key === 'categories' && view === 'payments') {
          conditionalKey = 'paymentCategories';
        }
        if (key === 'sources' && view === 'payments') {
          conditionalKey = 'paymentSources';
        }

        // The SOC site_of_care filter arrives hierarchical
        // ([{title, value, subtypes: [{title, value}]}]). Flatten into the
        // standard {title, value}[] shape used by the rest of the UI, with
        // subtype rows carrying composite "type__subtype" values and
        // "Type > Subtype" titles so click-through and the SOC picker
        // round-trip cleanly.
        const flattenedFilters =
          key === 'site_of_care'
            ? (filters || []).flatMap((row) => {
                const typeRow = { title: row.title, value: row.value };
                const subtypeRows = (row.subtypes || []).map((sub) => ({
                  title: `${row.title} > ${sub.title}`,
                  value: `${row.value}${SOC_COMPOSITE_SEPARATOR}${sub.value}`,
                }));
                return [typeRow, ...subtypeRows];
              })
            : [...filters];

        accum[key] = {
          title: defaultFilters[conditionalKey]?.title,
          filters: flattenedFilters.sort((a, b) => b.value - a.value),
          selectedDict: createSelectedDict(
            flattenedFilters || [],
            searchFilters,
            view,
            key
          ),
        };

        return accum;
      }, {} as FilterSet);

      return attributeFilters;
    },
    [prospectFilters, view]
  );

  const transformProfileFiltersToLegacy = useCallback(
    (profileFilters, viewContext) => {
      if (!profileFilters || !viewContext) return [];

      // Map view names to possible filter types
      const viewToFilterTypesMap = {
        cpt: ['cpt', 'code_cpt', 'code'],
        drg: ['drg', 'code_drg', 'code'],
        icdp: ['icd', 'code_icd', 'code'],
        hcpcs: ['hcpcs', 'code_hcpcs', 'code'],
        drug: ['drug', 'prescription', 'prescriptions'],
      };

      // Get the filter types for the current view
      const filterTypesForView = viewToFilterTypesMap[
        viewContext.toLowerCase()
      ] || [viewContext.toLowerCase(), 'code'];

      return profileFilters
        .filter((filter) => {
          if (!filter.type) return false;

          const filterTypeLower = filter.type.toLowerCase();

          // Check if the filter type matches any of the allowed types for this view
          return filterTypesForView.some(
            (type) => filterTypeLower === type || filterTypeLower.includes(type)
          );
        })
        .flatMap((filter) => {
          if (filter.values) {
            return filter.values.map((value) => ({
              context: filter.type.toLowerCase(),
              title: value.split('-')[1] || value, // "CPT-94726" -> "94726"
              value: value, // "CPT-94726"
            }));
          }
          return [];
        });
    },
    []
  );

  const parseCodeFilters = useCallback(() => {
    if (view === 'payments') {
      return;
    }

    // Use new filter sets if we have a selected profile
    if (selectedFilterSet?.filters) {
      const appliedCodes = transformProfileFiltersToLegacy(
        selectedFilterSet.filters,
        view
      );

      return {
        code: {
          title: selectedFilterSet.label || 'Profile Filters',
          filters: appliedCodes,
          selectedDict: appliedCodes.reduce((acc, filter) => {
            acc[filter.value] = true;
            return acc;
          }, {}),
        },
      };
    }

    // If no saved volume is selected, don't show any code filters
    return;
  }, [view, selectedFilterSet, transformProfileFiltersToLegacy]);

  const parsePrescriptionFilters = useCallback(() => {
    if (view !== 'prescriptions') {
      return;
    }

    // Use new filter sets if we have a selected profile
    if (selectedFilterSet?.filters) {
      const appliedDrugs = transformProfileFiltersToLegacy(
        selectedFilterSet.filters,
        'drug'
      );

      return {
        drug: {
          title: selectedFilterSet.label || 'Profile Filters',
          filters: appliedDrugs,
          selectedDict: appliedDrugs.reduce((acc, filter) => {
            acc[filter.value] = true;
            return acc;
          }, {}),
        },
      };
    }

    // If no saved volume is selected, don't show any drug filters
    return;
  }, [view, selectedFilterSet, transformProfileFiltersToLegacy]);

  const parseCategoryFilters = useCallback(
    (attributeFilters) => {
      if (!categories || !attributeFilters.categories) return;

      const appliedCategories = Array.isArray(categories)
        ? categories.reduce((accum, v) => {
            accum[v] = true;
            return accum;
          }, {})
        : { [categories]: true };
      const categoryFilters = {
        categories: {
          title: attributeFilters.categories.title,
          filters: attributeFilters.categories.filters,
          selectedDict: {
            ...attributeFilters.categories.selectedDict,
            ...appliedCategories,
          },
        },
      };

      return categoryFilters;
    },
    [categories]
  );

  useEffect(() => {
    if (!data?.filters) return;

    const attributeFilters = parseAttributeFilters(data.filters);
    const codeFilters = parseCodeFilters();
    const prescriptionFilters = parsePrescriptionFilters();
    const categoryFilters = parseCategoryFilters(attributeFilters);

    setFilters({
      ...codeFilters,
      ...prescriptionFilters,
      ...attributeFilters,
      ...categoryFilters,
    });

    return () => setFilters(defaultFilters);
  }, [
    data,
    setFilters,
    parseAttributeFilters,
    parseCodeFilters,
    parsePrescriptionFilters,
    parseCategoryFilters,
  ]);

  return <FilterContext.Provider value={filterState} {...props} />;
};

export const useFilters = () => {
  const context = useContext(FilterContext);
  const [filters, setFilters] = context;

  function toggleFilter(group, key) {
    const currentValue = filters[group].selectedDict[key];
    const appliedFilters = {
      ...filters,
      [group]: {
        ...filters[group],
        selectedDict: {
          ...filters[group].selectedDict,
          [key]: !currentValue,
        },
      },
    };

    setFilters(appliedFilters);
  }

  function clearFilters(group) {
    const appliedFilters = {
      ...filters,
      [group]: {
        ...filters[group],
        selectedDict: {},
      },
    };

    setFilters(appliedFilters);
  }

  if (!context) {
    throw new Error(
      'useFilters must be used inside a FilterProvider component'
    );
  }

  // Number of codes currently selected across the code-bearing groups
  // (`code` and `drug`, which back every code-bearing profile view). This is
  // the size of the code set sent as a query param to the API.
  const selectedCodeCount = getSelectedCodeCount(filters);
  const exceedsCodeLimit = selectedCodeCount > MAX_PROFILE_CODE_SET_SIZE;

  return {
    filters,
    setFilters,
    toggleFilter,
    clearFilters,
    selectedCodeCount,
    exceedsCodeLimit,
  };
};

/**
 * Counts the codes selected across the code-bearing filter groups. These are
 * the values emitted as the `code` / `drug` query param on any profile view
 * that sends codes.
 */
export function getSelectedCodeCount(filters: FilterSet) {
  return CODE_FILTER_GROUPS.reduce((count, group) => {
    const selectedDict = filters?.[group]?.selectedDict ?? {};
    return (
      count + Object.values(selectedDict).filter((isSelected) => isSelected).length
    );
  }, 0);
}

function createSelectedDict(
  allFilters,
  prospectFilters,
  view,
  filterType = ''
) {
  if (prospectFilters.length === 0) {
    return {};
  }

  // Place of Service / Site of Care filters from Discovery (context: 'site')
  // should not carry over to Profile Place of Service / Site of Care filters.
  if (filterType === 'place_of_service' || filterType === 'site_of_care') {
    return {};
  }

  // Match on ID
  const searchFilterValues = prospectFilters.map((filter) => filter.value);
  // Match on Title (payments)
  const searchFilterTitles = prospectFilters.map((filter) => filter.title);

  // Views in which we match on title
  const titleMatchViews = [
    'payments',
    // 'drug',
    'cpt',
    'icd',
    'icdp',
    'icd10d',
    'affiliations',
  ];

  return allFilters.reduce((result, { value, title }) => {
    result[value] = titleMatchViews.includes(view)
      ? searchFilterTitles.indexOf(title) >= 0
      : searchFilterValues.indexOf(value) >= 0;

    return result;
  }, {});
}
